import { all, call, put, select, takeEvery, takeLatest } from 'redux-saga/effects';
import * as loadImage from 'blueimp-load-image';

import { profileActions, profileInfoSelector, profileSelector } from './index';
import {
  ChangeContentPropertyAction,
  ChangeDomainAction,
  ChangeGenderFilterAction,
  CheckDomainAction,
  EditProfileAction,
  FollowAction,
  LoadFilteredPostsAction,
  LoadMorePostsAction,
  LoadProfileAction,
  SaveCountriesAction,
  SaveNotifySettingsAction,
  SendInviteAction,
  SendProfileViewAction,
  SetStatusAction,
  ToggleIsWatermarkEnabledAction,
  UnfollowAction,
  UploadCoverAction,
  UploadPhotoAction,
} from './interface';
import { Api } from '$api';
import { mainActions, mainSelector } from '../main';
import { postsActions } from '../posts';
import { toastActions } from '$store/toast';
import { goBack } from '$navigation/helper';
import {
  _confirm,
  canvasToBlob,
  imageLoad,
  releaseCanvas,
  setStorageItem,
  statReachGoal,
  validatePhone,
} from '$utils';
import { RootState } from '$store/rootReducer';
import { groupsActions } from '$store/groups';
import { authSelector } from '$store/auth';
import { batchActions } from '$store';
import { followersActions } from '$store/followers';
import { feedActions } from '$store/feed';
import { initWorker } from '$store/auth/sagas';
import router from '$navigation/router';
import { ContentPriorityType } from '$store/main/interface';
import { attachActions, attachItemsSelector } from '$store/attach';
import { AttachItem } from '$store/models';
import { blacklistActions } from '$store/blacklist';
import { bundlesActions } from '$store/bundles';
import { reelsActions } from '$store/reels';
import { t } from '$translation';

function* loadProfileWorker(action: LoadProfileAction) {
  const { ownerId, filter } = action.payload;
  try {
    const data = yield call(Api.get, `/profile/${ownerId}`);
    yield handleProfileResult(ownerId, data);

    const user = data.users[ownerId];
    if (filter && !(!user || user.isolated)) {
      // Это конечно хак, лучше сделать это все в один запрос `/profile/${ownerId}`
      // Но пока на бэке это не реализовано. Кейс редкий, так что пока так

      yield loadFilteredPostsWorker({
        type: action.type,
        payload: {
          ownerId,
          filter: filter || '',
        },
      });
    }
  } catch (e) {
    yield put(
      profileActions.setProfileFailed({
        userId: ownerId,
        code: e.code,
      }),
    );
  }
}

export function* handleProfileResult(profileId: number, data: any) {
  if (data.isMediaUser) {
    yield call(setStorageItem, 'contentPriorityType', ContentPriorityType.OTHER);
  } else {
    yield call(setStorageItem, 'contentPriorityType', ContentPriorityType.ADULT);
  }

  // if (data.isMediaUser && location.hostname === 'ton.place') {
  //   const { decryptKeys } = yield select(mainSelector);
  //   if (!decryptKeys[-1]) {
  //     location.href = location.href.replace('ton.place', 'tonplace.io');
  //     return;
  //   }
  // }

  yield put(
    batchActions(
      mainActions.setUsers(data.users),
      mainActions.setGroups(data.groups),
      blacklistActions.setBanToMe({
        userId: profileId,
        isBanned: data.isBannedFromUser,
      }),
      postsActions.setPosts(data.posts),
      postsActions.setList({
        listId: String(profileId),
        items: data.posts.map((post) => post.id),
      }),
      bundlesActions.setBundles(data.bundles),
      profileActions.setProfile({
        userId: profileId,
        postsCount: +data.posts_count,
        reelsCount: +data.reels_count,
        isFollowFromMe: data.is_follow_from_me,
        isFollowToMe: data.is_follow_to_me,
        postsNextFrom: data.postsNextFrom,
        scheduledCount: data.scheduledCount,
        animeListsCount: data.animeListsCount,
        sponsoredPostsCount: data.sponsoredPostsCount,
        totalLikesCount: data.totalLikesCount,
        subscriptionMessage: data.subscriptionMessage,
        subscriptionPrice: +data.subscriptionPrice,
        nftAddress: data.nftAddress,
        coverUrl: data.coverUrl,
        photoWidth: data.photoWidth,
        photoHeight: data.photoHeight,
        coverThumbnail: data.coverThumbnail,
        bundleIds: data.bundles ? data.bundles.map((b) => b.id) : [],
        coverId: data.coverId,
        sponsorsContentCount: data.sponsorsContentCount || { photos: 0, videos: 0 },
        subscriptionEurPrice: data.subscriptionEurPrice,
        subscriptionDiscount: data.subscriptionDiscount,
        welcomeVideo: data.welcomeVideo,
        stream: data.stream,
        isBannedFromUser: data.isBannedFromUser,
        squad: data.squad,
        leaderboardPosition: data.leaderboardPosition,
        wonLeaderboards: data.wonLeaderboards || [],
      }),
    ),
  );
}

function* followWorker(action: FollowAction) {
  const { recommendations, id: userId } = action.payload;
  try {
    yield put(
      batchActions(
        profileActions.setFollowStatus({ userId, isFollowed: true }),
        followersActions.setFollowing({
          [userId]: true,
        }),
      ),
    );
    const { user, authors } = yield call(
      Api.post,
      `/follow/${userId}/add?recommended_authors=${recommendations ? 1 : 0}`,
    );
    yield put(
      batchActions(
        mainActions.setUsers([user, ...authors]),
        followersActions.setRecommendedAuthors({
          userId,
          authors: authors.map((author) => author.id),
        }),
        followersActions.setFollowing({
          [userId]: true,
        }),
      ),
    );
    yield call(statReachGoal, 'follow');
  } catch (e) {
    yield put(
      batchActions(
        profileActions.setFollowStatus({ userId, isFollowed: false }),
        followersActions.setFollowing({
          [userId]: false,
        }),
      ),
    );
    yield put(toastActions.setToastFail(e.message));
  }
}

function* unfollowWorker(action: UnfollowAction) {
  const userId = action.payload;
  try {
    yield put(
      batchActions(
        profileActions.setFollowStatus({ userId, isFollowed: false }),
        followersActions.setFollowing({
          [userId]: false,
        }),
        reelsActions.deleteUserFollow(userId),
      ),
    );
    const resp = yield call(Api.post, `/follow/${userId}/del`);
    yield put(
      batchActions(
        mainActions.setUser(resp.user),
        followersActions.setFollowing({
          [userId]: false,
        }),
      ),
    );
  } catch (e) {
    yield put(
      batchActions(
        profileActions.setFollowStatus({ userId, isFollowed: true }),
        followersActions.setFollowing({
          [userId]: true,
        }),
      ),
    );
  }
}

function* editProfileWorker(action: EditProfileAction) {
  try {
    yield put(toastActions.setToastLoading());

    const { firstName, lastName, sex, birthday, countryId, cityId, bio } = action.payload;

    const bDateExp = birthday.split('.');
    const bDay = +bDateExp[0] || 0;
    const bMonth = +bDateExp[1] || 0;
    const bYear = +bDateExp[2] || 0;

    const { user, bioModeration } = yield call(Api.post, '/profile/edit', {
      firstName,
      lastName,
      sex,
      bDay,
      bMonth,
      bYear,
      countryId,
      cityId,
      bio,
    });

    if (user) {
      yield put(mainActions.setUser(user));
    }

    if (bioModeration) {
      yield put(toastActions.hideToast());
      yield call(_confirm, t('profile_bio_moderation_alert'));
    } else {
      yield put(toastActions.setToastSuccess());
      yield call(goBack);
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* setStatusWorker(action: SetStatusAction) {
  try {
    const { text, isPublish, groupId } = action.payload;

    const endpoint = groupId > 0 ? `/group/${groupId}/status` : '/profile/status';
    yield put(toastActions.setToastLoading());
    const resp = yield call(Api.post, endpoint, {
      status: text,
      publish: isPublish,
    });
    if (groupId > 0) {
      yield put(mainActions.setGroup(resp.group));
      yield put(groupsActions.loadGroupInfo(groupId));
    } else {
      if (resp.user) {
        yield put(mainActions.setUser(resp.user));
        yield put(
          profileActions.loadProfile({
            ownerId: resp.user.id,
          }),
        );
      } else if (resp.moderation) {
        yield put(toastActions.hideToast());
        yield call(_confirm, t('profile_status_moderation_alert'));
      }
    }
    yield put(toastActions.hideToast());
    yield call(goBack);
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* sendInviteWorker(action: SendInviteAction) {
  try {
    const phone = action.payload.phone.trim();

    if (!validatePhone(phone)) {
      yield put(toastActions.setToastFail('Введите корректный номер телефона'));
      return;
    }

    yield put(toastActions.setToastLoading());
    const resp = yield call(Api.post, '/invite', {
      phoneCode: +action.payload.code,
      phone: phone.replace(/[\s()\-]/g, ''),
    });
    yield put(mainActions.setUser(resp.user));
    yield put(toastActions.setToastSuccess());
    action.payload.onDone();
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* loadMorePostsWorker(action: LoadMorePostsAction) {
  try {
    const { ownerId, filter } = action.payload;
    const { postsNextFrom } = yield select((state: RootState) =>
      profileInfoSelector(state, ownerId),
    );
    const data = yield call(Api.post, `/profile/${ownerId}/posts`, {
      startFrom: +postsNextFrom,
      onlySponsored: filter?.onlySponsored || false,
      onlyErotic: filter?.onlyErotic || false,
      onlyFree: filter?.onlyFree || false,
      showReplies: filter?.showReplies || false,
    });

    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        mainActions.setGroups(data.groups),
        postsActions.setPosts(data.posts),
        postsActions.setTotal(data.total),
        postsActions.pushToList({
          listId: String(ownerId),
          items: data.posts.map((post) => post.id),
        }),
        profileActions.setLoadMorePosts({
          userId: ownerId,
          nextFrom: data.nextFrom,
        }),
      ),
    );
  } catch (e) {}
}

function* loadFilteredPostsWorker(action: LoadFilteredPostsAction) {
  try {
    const { ownerId, filter } = action.payload;
    const data = yield call(Api.post, `/profile/${ownerId}/posts`, {
      startFrom: filter?.startFrom || 0,
      onlySponsored: filter?.onlySponsored,
      onlyErotic: filter?.onlyErotic,
      onlyFree: filter?.onlyFree,
      showReplies: filter?.showReplies,
    });

    yield put(
      batchActions(
        mainActions.setUsers(data.users),
        mainActions.setGroups(data.groups),
        postsActions.setPosts(data.posts),
        postsActions.setTotal(data.total),
        postsActions.setList({
          listId: String(ownerId),
          items: data.posts.map((post) => post.id),
        }),
        profileActions.setFilteredPostsLoaded({ ownerId, nextFrom: data.nextFrom }),
      ),
    );
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* uploadPhotoWorker(action: UploadPhotoAction) {
  try {
    const file = action.payload;

    const { currentUserId } = yield select(authSelector);

    if (file.type.substr(0, 6) !== 'image/' || file.type === 'image/gif') {
      return yield put(toastActions.setToastFail('Это не фото — нужно фото'));
    }

    yield put(toastActions.setToastLoading());

    const img = yield call(loadImage, file, {
      maxWidth: 1920,
      maxHeight: 1920,
      meta: true,
      canvas: true,
      imageSmoothingEnabled: true,
      imageSmoothingQuality: 'high',
      orientation: true,
    });

    const quality = 0.8;
    const blob = yield call(canvasToBlob, img.image, quality);

    const form = new FormData();
    form.append('file', blob);
    const data = yield call(Api.post, `/profile/photo`, form);
    if (data.moderation) {
      yield put(toastActions.hideToast());
      yield call(_confirm, t('profile_photo_moderation_alert'));
    } else {
      yield put(mainActions.setUser(data.user));
      yield put(toastActions.setToastSuccess());
      yield put(
        profileActions.loadProfile({
          ownerId: currentUserId,
        }),
      );
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* checkDomainWorker(action: CheckDomainAction) {
  try {
    yield call(Api.post, '/domain/check', {
      domain: action.payload.domain,
    });
    action.payload.onDone();
  } catch (e) {
    action.payload.onFail(e.message);
  }
}

function* changeDomainWorker(action: ChangeDomainAction) {
  try {
    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/profile/domain', {
      domain: action.payload,
    });
    data.user.domain = action.payload;
    yield put(mainActions.setUser(data.user));
    yield call(goBack);
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* changePasswordWorker(action: ChangeDomainAction) {
  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, '/profile/password', {
      password: action.payload,
    });
    yield put(mainActions.setHasPassword(true));
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* resetPasswordWorker() {
  try {
    yield put(mainActions.setHasPassword(false));
    yield call(Api.post, '/profile/password/reset');
  } catch (e) {
    yield put(mainActions.setHasPassword(true));
    yield put(toastActions.setToastFail(e.message));
  }
}

function* changeContentPriorityWorker(action: ChangeContentPropertyAction) {
  const { contentPriority: prevContentPriority } = yield select(mainSelector);
  const { contentPriority, section, needReload, isStartScreen = false } = action.payload;

  try {
    // yield put(feedActions.clearFeed());
    yield put(toastActions.setToastLoading());
    yield put(mainActions.setContentPriority(contentPriority));
    yield call(Api.post, '/profile/content_priority', {
      contentPriority,
    });
    yield put(toastActions.hideToast());
    yield initWorker();
    if (section) {
      yield put(
        feedActions.loadFeed({
          section: section,
          query: '',
        }),
      );
    } else {
      yield put(feedActions.clearFeed());
    }

    // if (isStartScreen && contentPriority === ContentPriorityType.ADULT) {
    //   showModal('intro');
    // }

    if (needReload) {
      location.reload();
    }
  } catch (e) {
    yield put(toastActions.setToastFail());
    yield put(mainActions.setContentPriority(prevContentPriority));
  }
}

function* changeGenderPriorityWorker(action: ChangeGenderFilterAction) {
  const { genderFilter: prevGenderFilter } = yield select(mainSelector);
  const { genderFilter, needReloadFeed, needClearFeed } = action.payload;

  try {
    yield put(mainActions.setGenderFilter(genderFilter));
    yield call(Api.post, '/profile/gender_filter', {
      gender: genderFilter,
    });
    if (needClearFeed) {
      yield put(feedActions.clearFeed());
    }
    if (needReloadFeed) {
      const { params } = router.getState();
      yield put(
        feedActions.loadFeed({
          section: params.section,
          query: '',
        }),
      );
    }
  } catch (e) {
    yield put(toastActions.setToastFail());
    yield put(mainActions.setGenderFilter(prevGenderFilter));
  }
}

function* saveCountriesWorker(action: SaveCountriesAction) {
  try {
    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/profile/countries', {
      countries: action.payload,
    });
    yield put(
      mainActions.setUser({
        ...data.user,
        availCountries: data.user.availCountries || [],
      }),
    );
    yield call(goBack);
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* saveNotifySettingsWorker(action: SaveNotifySettingsAction) {
  try {
    const {
      enabled,
      messages,
      soldMessage,
      comment,
      allowCommentFromUnfollowed,
      like,
      allowLikeFromUnfollowed,
      follow,
      gift,
    } = action.payload;

    yield put(toastActions.setToastLoading());
    const data = yield call(Api.post, '/profile/save_notify', {
      enabled,
      messages,
      soldMessage,
      comment,
      allowCommentFromUnfollowed,
      like,
      allowLikeFromUnfollowed,
      follow,
      gift,
    });
    yield put(mainActions.setUser(data.user));
    yield call(goBack);
    yield put(toastActions.setToastSuccess());
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  }
}

function* toggleIsWatermarkEnabledWorker(action: ToggleIsWatermarkEnabledAction) {
  const currentIsWatermarkEnabledValue = action.payload;
  yield put(mainActions.setIsWatermarkDisabled(currentIsWatermarkEnabledValue));

  try {
    const data = yield call(Api.post, '/profile/watermark', {
      enabled: !currentIsWatermarkEnabledValue,
    });
    yield put(mainActions.setUser(data.user));
  } catch (e) {
    yield put(mainActions.setIsWatermarkDisabled(!currentIsWatermarkEnabledValue));
    yield put(toastActions.setToastFail(e.message));
  }
}

function* deleteCoverWorker() {
  const { userId, profileInfo } = yield select(profileSelector);
  const { coverUrl, coverThumbnail } = profileInfo[userId];
  yield put(profileActions.deleteProfileCover());
  try {
    yield call(Api.post, '/profile/cover/delete');
  } catch (e) {
    yield put(
      profileActions.setProfileCover({
        coverUrl,
        coverThumbnail,
      }),
    );
    yield put(toastActions.setToastFail(e.message));
  }
}

function* uploadCoverWorker(action: UploadCoverAction) {
  const { offsetY, offsetX, isWide, coverUrl, isAdCover = false } = action.payload;

  const canvas = document.createElement('canvas');

  try {
    const ctx = canvas.getContext('2d');

    yield put(toastActions.setToastLoading());
    const img = yield call(imageLoad, coverUrl);
    if (ctx) {
      canvas.width = Math.min(img.width, 568 * 2);
      canvas.height = canvas.width * (9 / 16);
      const rate = isWide ? canvas.height / img.height : canvas.width / img.width;
      const displayHeight =
        canvas.height > img.height ? canvas.height : img.height * rate;
      const displayWidth = img.width * rate;

      ctx.drawImage(
        img,
        displayWidth * offsetX,
        displayHeight * offsetY,
        displayWidth,
        displayHeight,
      );
      const blob = yield call(canvasToBlob, canvas, 0.85);
      if (blob) {
        const formData = new FormData();
        formData.append('file', blob);
        if (isAdCover) {
          yield put(
            attachActions.uploadPhotos({
              pickerId: 'post_ad_new',
              files: [blob],
              watermark: false,
            }),
          );
        } else {
          const cover = yield call(Api.post, '/profile/cover', formData);
          if (cover.moderation) {
            yield put(toastActions.hideToast());
            yield call(_confirm, t('profile_cover_moderation_alert'));
          } else {
            yield put(profileActions.setProfileCover(cover));
          }
        }
        yield put(toastActions.setToastSuccess());
        goBack();
      }
    }
  } catch (e) {
    yield put(toastActions.setToastFail(e.message));
  } finally {
    releaseCanvas(canvas);
  }
}

function* uploadWelcomeVideoWorker() {
  const { userId } = yield select(profileSelector);
  const attachments: AttachItem[] = yield select((state: RootState) =>
    attachItemsSelector(state, `welcome_video`),
  );

  const videoId = attachments[0].video?.videoId;
  if (!videoId) return;
  try {
    yield put(toastActions.setToastLoading());
    const { moderation } = yield call(Api.post, `/profile/welcome_video`, {
      videoId,
    });
    if (moderation) {
      yield put(toastActions.hideToast());
      yield call(_confirm, t('profile_welcome_video_moderation_alert'));
    } else {
      const data = yield call(Api.get, `/profile/${userId}`);
      yield call(handleProfileResult, userId, data);
      yield put(profileActions.setWelcomeVideoEdit(false));
      yield put(toastActions.setToastSuccess());
      yield put(attachActions.clear('welcome_video'));
    }
  } catch (error) {
    yield put(toastActions.setToastFail(error.message));
  }
}

function* handleDeleteVideoWorker() {
  try {
    yield put(toastActions.setToastLoading());
    yield call(Api.post, `/profile/welcome_video/delete`);
    yield put(profileActions.deleteWelcomeVideo());
    yield put(profileActions.setWelcomeVideoEdit(false));
    yield put(toastActions.setToastSuccess());
  } catch (error) {
    yield put(toastActions.setToastFail(error.message));
  }
}

function* sendProfileViewWorker(action: SendProfileViewAction) {
  try {
    yield call(Api.post, `/profile/${action.payload}/view`);
  } catch (e) {}
}

export function* profileSaga() {
  yield all([
    takeEvery(profileActions.loadProfile, loadProfileWorker),
    takeLatest(profileActions.follow, followWorker),
    takeLatest(profileActions.unfollow, unfollowWorker),
    takeLatest(profileActions.editProfile, editProfileWorker),
    takeLatest(profileActions.setStatus, setStatusWorker),
    takeLatest(profileActions.sendInvite, sendInviteWorker),
    takeLatest(profileActions.loadMorePosts, loadMorePostsWorker),
    takeLatest(profileActions.loadFilteredPosts, loadFilteredPostsWorker),
    takeLatest(profileActions.uploadPhoto, uploadPhotoWorker),
    takeLatest(profileActions.checkDomain, checkDomainWorker),
    takeLatest(profileActions.changeDomain, changeDomainWorker),
    takeLatest(profileActions.changePassword, changePasswordWorker),
    takeLatest(profileActions.resetPassword, resetPasswordWorker),
    takeLatest(profileActions.changeContentPriority, changeContentPriorityWorker),
    takeLatest(profileActions.changeGenderPriority, changeGenderPriorityWorker),
    takeLatest(profileActions.saveCountries, saveCountriesWorker),
    takeLatest(profileActions.saveNotifySettings, saveNotifySettingsWorker),
    takeLatest(profileActions.toggleIsWatermarkEnabled, toggleIsWatermarkEnabledWorker),
    takeLatest(profileActions.deleteCover, deleteCoverWorker),
    takeLatest(profileActions.uploadCover, uploadCoverWorker),
    takeLatest(profileActions.uploadWelcomeVideo, uploadWelcomeVideoWorker),
    takeLatest(profileActions.requestDeleteWelcomeVideo, handleDeleteVideoWorker),
    takeLatest(profileActions.sendProfileView, sendProfileViewWorker),
  ]);
}
